/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { AnimationClassNames, getFocusStyle, getTheme, mergeStyles } from '@fluentui/react/lib/Styling';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getStylist } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { TestBladeActionBar } from 'src/luis/projects/testPane/components/common/TestBladeActionBar';
import { getTestPaneTopAction } from 'src/luis/projects/testPane/components/common/TestPaneTopAction';
import { getTopScoringIntent } from 'src/luis/projects/testPane/utils/getTopScoringIntent';

export type ChatBladeProps = {
    /**
     * Getter for the selected utterance.
     */
    selectedUtteranceId: React.ReactText;

    /**
     * Setter for the selected utterance.
     */
    onSelectUtterance: (selectedUtteranceId: React.ReactText) => void;

    /**
     * Called when the user wants to inspect a chat item.
     */
    onInspectResult: () => void;

    /**
     * Called when the user wants to open the batch testing panel.
     */
    onOpenBatchTestingPanel: () => void;

    /**
     * Called when the user starts over.
     */
    onStartOver: () => void;
};

const messages = defineMessages({
    inspect: {
        id: 'testPane.chatBlade.inspect',
        defaultMessage: 'Inspect'
    },
    batchTestPanel: {
        id: 'testPane.chatBlade.batchTestPanel',
        defaultMessage: 'Batch testing panel'
    },
    startOver: {
        id: 'testPane.chatBlade.startOver',
        defaultMessage: 'Start over'
    },
    dirtyVersionMessage: {
        id: 'testPane.chatBlade.dirtyVersionMessage',
        defaultMessage: 'You have untrained changes that will not be reflected in testing.'
    },
    testFieldPlaceholder: {
        id: 'testPane.chatBlade.testFieldPlaceholder',
        defaultMessage: 'Type a test utterance ...'
    }
});

const { styleComponent } = getStylist('ChatBlade');
const UtteranceBubble = styleComponent<IStackProps>(Stack)<{ isSelected: boolean }>(
    'UtteranceBubble',
    theme => ({
        color: theme.utteranceTextColor,
        padding: theme.utterancePadding,
        cursor: 'pointer'
    }),
    ({ customProps }, theme) => ({
        backgroundColor: customProps.isSelected ? theme.selectedUtteranceBackgroundColor : theme.utteranceBackgroundColor
    })
);

const focusClassName = mergeStyles(getFocusStyle(getTheme()));
const StartOverAction = getTestPaneTopAction('left', 'dark');
const OpenBatchTestingAction = getTestPaneTopAction('right', 'dark');

/**
 * Renders the chat blade (the first blade) in
 * the test pane. Users can add utterances and
 * test them for further debugging.
 */
export const ChatBlade = observer((props: ChatBladeProps) => {
    const { selectedUtteranceId, onSelectUtterance, onInspectResult, onOpenBatchTestingPanel, onStartOver } = props;
    const { dispatcher, rootStore } = useGlobalState();
    const [chatInput, setChatInput] = React.useState('');
    const { entities } = useEntities();
    const { localize } = useLocalization();

    const predictionStoreHouse = rootStore.currentLuisAppStore.currentAppVersionStore.testPanePredictionStoreHouse;
    const { items: predictions, isLoading: arePredictionsLoading } = predictionStoreHouse;
    const isVersionDirty = rootStore.currentLuisAppStore.currentAppVersionStore.isDirty;

    const tracker = useProgressTracker({
        onComplete: () => {
            onSelectUtterance(predictions[0].id);
            setChatInput('');
        }
    });

    /**
     * Clears the prediction store house test utterances.
     */
    const onStartOverClick = () => {
        dispatcher.dispatch('clearTestUtterances');
        onStartOver();
    };

    /**
     * Submits the value of the text field if valid
     * when the enter key is pressed.
     */
    const onEnterKeyUp = () => {
        if (chatInput.trim().length === 0) {
            return;
        }

        dispatcher.dispatch('predictTestUtterance', { text: chatInput, entities, tracker });
    };

    /**
     * Sets the current utterance text as the selected text.
     *
     * @param utteranceId The text that was selected.
     */
    const onInspectUtteranceClick = (utteranceId: string) => {
        onSelectUtterance(utteranceId);
        onInspectResult();
    };

    /**
     * Renders the utterance bubbles that are in
     * the prediction store house.
     */
    const renderUtteranceBubbles = () =>
        predictions.map(item => {
            const topScoreIntent = getTopScoringIntent(item.mainUtterance.utterance.predictedIntents);
            const utteranceText = item.mainUtterance.utterance.text;
            const utteranceId = item.id as string;
            const isSelected = utteranceId === selectedUtteranceId;

            return (
                <Stack key={utteranceId} verticalAlign="start" horizontalAlign="stretch" className={AnimationClassNames.fadeIn400}>
                    <UtteranceBubble
                        data-is-focusable="true"
                        className={focusClassName}
                        customProps={{ isSelected }}
                        role="button"
                        onClick={() => onInspectUtteranceClick(utteranceId)}>
                        <LuisText>{utteranceText}</LuisText>
                    </UtteranceBubble>

                    <Stack verticalAlign="center" horizontal horizontalAlign="space-between">
                        <LuisText variant="small" data-automation-id="TopScoringIntentText">
                            {topScoreIntent.intent.name} ({topScoreIntent.score.toFixed(3)})
                        </LuisText>

                        <LuisText variant="small">
                            <Link
                                data-is-focusable="true"
                                aria-label={localize(messages.inspect)}
                                onClick={() => onInspectUtteranceClick(utteranceId)}
                                styles={{ root: { padding: 4 } }}>
                                {localize(messages.inspect)}
                            </Link>
                        </LuisText>
                    </Stack>
                </Stack>
            );
        });

    return (
        <Stack>
            <TestBladeActionBar>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <StartOverAction disabled={predictions.length === 0} onClick={onStartOverClick}>
                        {localize(messages.startOver)}
                    </StartOverAction>

                    <OpenBatchTestingAction onClick={onOpenBatchTestingPanel}>{localize(messages.batchTestPanel)}</OpenBatchTestingAction>
                </Stack>
            </TestBladeActionBar>

            {isVersionDirty && (
                <MessageBar styles={{ root: { marginBottom: 10 } }} messageBarType={MessageBarType.warning}>
                    {localize(messages.dirtyVersionMessage)}
                </MessageBar>
            )}

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisTextField
                    autoFocus
                    value={chatInput}
                    placeholder={localize(messages.testFieldPlaceholder)}
                    ariaLabel={localize(messages.testFieldPlaceholder)}
                    onChange={(_, value) => (arePredictionsLoading ? null : setChatInput(value))}
                    onEnterKeyUp={onEnterKeyUp}
                    maxLength={500}
                    data-automation-id="TestUtteranceField"
                />

                <Stack tokens={{ childrenGap: 15 }}>
                    {arePredictionsLoading && (
                        <Stack verticalAlign="center" horizontal horizontalAlign="start" tokens={{ childrenGap: 10 }}>
                            <Spinner size={SpinnerSize.small} />
                            <LuisText>{localize(globalMessages.loading)}</LuisText>
                        </Stack>
                    )}
                    <FocusZone>
                        <Stack tokens={{ childrenGap: 10 }}>{renderUtteranceBubbles()}</Stack>
                    </FocusZone>
                </Stack>
            </Stack>
        </Stack>
    );
});
